<template>
    <LoadingPanel
        :headline="t('generators.portfolioOverview.plantStatusSummary.headline')"
        :is-pending="isPending"
        :is-error="isError"
    >
        <div class="plant-status-summary">
            <FormSingleSelectField
                v-model="plantGroupSid"
                :placeholder-text="
                    t('generators.portfolioOverview.plantStatusSummary.selector.placeholders.plantGroup')
                "
                class="plant-status-summary__field"
                :options="plantGroupOptions"
            />
            <div
                v-if="plantGroupSid === undefined"
                class="plant-status-summary__empty-state"
            >
                <pbl-icon name="check2-square" />
                <span>
                    {{ t('generators.portfolioOverview.plantStatusSummary.emptyState') }}
                </span>
            </div>
            <PortfolioOverviewPlantStatusSummaryChart
                v-else
                :plant-group-sid="plantGroupSid"
            />
        </div>
    </LoadingPanel>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { usePortfolioGroupSummaryQuery } from '@/api/queries/generators/portfolio-overview/usePortfolioGroupSummaryQuery'
import FormSingleSelectField from '@/components/form-inputs/form-single-select-field/FormSingleSelectField.vue'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import PortfolioOverviewPlantStatusSummaryChart from '@/pages/generators/portfolio-overview/plant-status-summary/PortfolioOverviewPlantStatusSummaryChart.vue'
import { getComponentPlantGroupSid, setComponentPlantGroupSid } from '@/utils/plant-status-summary-utils'
import { ComponentType } from '@/configuration/component/component-types'

const emit = defineEmits<{
    'reset:group': []
}>()

const props = defineProps<{
    plantGroupSidParam?: number
}>()

const plantGroupSid = ref<number | undefined>(getComponentPlantGroupSid(ComponentType.Generator))

const { t } = useI18n()
const { data, isPending, isError } = usePortfolioGroupSummaryQuery()

const plantGroups = computed(() => {
    if (!data.value?.plantGroups) {
        return []
    }

    return data.value.plantGroups
})

const plantGroupSids = computed(() => {
    if (!data.value?.plantGroupSids) {
        return []
    }

    return data.value.plantGroupSids
})

const plantGroupOptions = computed<SelectOptions<number>>(() =>
    plantGroupSids.value
        .map((value, index) => ({
            label: plantGroups.value[index] ?? value,
            value: value ?? 0,
        }))
        .reverse(),
)

watch(
    () => props.plantGroupSidParam,
    (newPlantGroupSid) => {
        if (newPlantGroupSid !== undefined) {
            plantGroupSid.value = newPlantGroupSid
            emit('reset:group')
        }
    },
)

watch(
    plantGroupSids,
    (newPlantGroupSids) => {
        if (plantGroupSid.value === undefined) {
            plantGroupSid.value = newPlantGroupSids.find((e) => e !== undefined)
        }
    },
    { immediate: true },
)

watch(plantGroupSid, (newPlantGroupSid) => {
    setComponentPlantGroupSid(ComponentType.Generator, newPlantGroupSid)
})
</script>

<style scoped lang="scss">
.plant-status-summary {
    &__field {
        width: 240px;
        margin-bottom: $spacing-m;
    }

    &__empty-state {
        padding: 50px 0;

        display: flex;
        flex-direction: column;

        align-items: center;
        justify-content: center;
        gap: $spacing-s;

        color: $pbl-text-muted;

        pbl-icon {
            --size: 2rem;
        }
    }
}
</style>
